import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { SecretBulkResult, SecretList } from '@one-deployment/api'
import { Button, Input, Select, SkeletonRows, Textarea } from '@/components/ui'
import { Button as SButton } from '@/components/shadcn/button'
import { Checkbox } from '@/components/shadcn/checkbox'
import { ErrorState } from '@/components/status'
import { RoleGate, useCan } from '@/components/RoleGate'
import { del, enc, get, post, put } from '@/lib/api'
import { cn } from '@/lib/cn'
import { qk } from '@/lib/query'
import { ask } from '@/components/ask'

type Wanted = NonNullable<SecretList['file']>['wanted'][number]

const COL: Record<string, { label: string; hint: string }> = {
  '': { label: 'All environments', hint: 'Used everywhere unless an environment below has its own value' },
  local: { label: 'dev', hint: "Claude's container: its environment and the apps' .env files" },
  uat: { label: 'uat', hint: 'The uat deploy' },
  prod: { label: 'prod', hint: 'Production' },
}
const col = (c: string) => COL[c] ?? { label: c, hint: c }

const GROUPS = [
  { kind: 'frontend', title: 'Frontend', hint: 'Shipped to the browser or app: anyone can read these' },
  { kind: 'backend', title: 'Backend', hint: 'Server only' },
  { kind: 'deploy', title: 'Deploy', hint: 'Only CI uses these, to build and deploy' },
  { kind: 'none', title: 'Not in the project file', hint: 'Stored, but the file does not say where they go' },
] as const

/** UAT_SUPABASE_URL is really SUPABASE_URL's uat value: offer to copy it there. */
const PREFIX: Record<string, string> = { UAT: 'uat', PROD: 'prod', PRODUCTION: 'prod', DEV: 'local', LOCAL: 'local' }
function splitPrefix(n: string): { env: string; base: string } | null {
  const m = /^([A-Z]+)_(.+)$/.exec(n)
  return m && PREFIX[m[1]!] ? { env: PREFIX[m[1]!]!, base: m[2]! } : null
}

type Filter = 'all' | 'missing' | 'extra'

/**
 * Settings → Secrets (features/23): a row per name, a column per
 * environment. An environment uses its own value, else "All environments".
 * View fetches one value on click (audited, never cached) and Hide drops it.
 */
export function SecretsTab({ project }: { project: string }) {
  const qc = useQueryClient()
  const canWrite = useCan('project.secrets.write', project)
  const { data, error, refetch } = useQuery({ queryKey: qk.secrets(project), queryFn: ({ signal }) => get<SecretList>(`/api/projects/${enc(project)}/secrets`, signal) })
  const [search, setSearch] = useState('')
  const [filter, setFilter] = useState<Filter>('all')
  const [name, setName] = useState('')
  const [value, setValue] = useState('')
  const [addEnv, setAddEnv] = useState('')
  const [editing, setEditing] = useState<string | null>(null)
  const [edit, setEdit] = useState('')
  // Revealed values live only here: dropped on Hide, on any change, and when the tab unmounts.
  const [shown, setShown] = useState<Record<string, string>>({})
  const [viewError, setViewError] = useState<unknown>(null)
  const key = (n: string, e: string) => `${n}@${e}`
  const q = (e: string) => (e ? `?env=${enc(e)}` : '')
  const base = `/api/projects/${enc(project)}/secrets`
  const hide = (k: string) => setShown(({ [k]: _, ...rest }) => rest)
  const view = (n: string, e: string) => {
    setViewError(null)
    get<{ value: string }>(`${base}/${enc(n)}/value${q(e)}`).then((r) => setShown((o) => ({ ...o, [key(n, e)]: r.value })), setViewError)
  }
  const done = () => { setName(''); setValue(''); setEditing(null); setEdit(''); setShown({}); void qc.invalidateQueries({ queryKey: qk.secrets(project) }) }
  const set = useMutation({ mutationFn: (b: { name: string; env: string; value: string }) => put(`${base}/${enc(b.name)}`, { value: b.value, env: b.env }), onSuccess: done })
  const remove = useMutation({ mutationFn: (b: { name: string; env: string }) => del(`${base}/${enc(b.name)}${q(b.env)}`), onSuccess: done })
  const copy = useMutation({ mutationFn: (b: { name: string; toName: string; toEnv: string }) => post(`${base}/${enc(b.name)}/copy`, { env: '', toName: b.toName, toEnv: b.toEnv }), onSuccess: done })
  const tick = useMutation({
    mutationFn: (b: { name: string; env: string; on: boolean }) => (b.on ? put(`${base}/${enc(b.name)}/marks/trigger${q(b.env)}`, {}) : del(`${base}/${enc(b.name)}/marks/trigger${q(b.env)}`)),
    onSuccess: () => void qc.invalidateQueries({ queryKey: qk.secrets(project) }),
  })
  const [paste, setPaste] = useState('')
  const [pasteEnv, setPasteEnv] = useState('')
  const bulk = useMutation({ mutationFn: (b: { text: string; env: string }) => post<SecretBulkResult>(`${base}/bulk`, b), onSuccess: () => { setPaste(''); done() } })

  const rows = data?.secrets
  const file = data?.file
  // Without a project file the usual three environments still get a column.
  const columns = ['', ...(file?.columns ?? ['local', 'uat', 'prod', ...new Set((rows ?? []).map((r) => r.env).filter((e) => e && !['local', 'uat', 'prod'].includes(e)))])]
  const stored = new Map((rows ?? []).map((r) => [key(r.name, r.env), r]))
  const marks = new Map((data?.marks ?? []).map((m) => [key(m.name, m.env), m]))
  const wanted = new Map((file?.wanted ?? []).map((w) => [w.name, w]))
  const names = [...new Set([...(file?.wanted ?? []).map((w) => w.name), ...(rows ?? []).map((r) => r.name)])].sort()
  const hasAny = (n: string) => columns.some((c) => stored.has(key(n, c)))
  const applies = (n: string, e: string) => {
    const w = wanted.get(n)
    if (!w || !e) return true
    return e === 'local' ? w.local : !w.envs || w.envs.includes(e)
  }
  /** A cell that needs a value: the file sends it there and neither it nor All has one. */
  const needs = (n: string, e: string) => !!wanted.get(n)?.required && e !== '' && applies(n, e) && !stored.has(key(n, e)) && !stored.has(key(n, ''))
  const missingCount = names.filter((n) => columns.some((c) => needs(n, c))).length
  const visible = names.filter((n) => n.includes(search.toUpperCase()))
    .filter((n) => filter === 'all' || (filter === 'missing' ? columns.some((c) => needs(n, c)) : !wanted.has(n)))
  // Where each column's values end up, for its header.
  const goesTo = (c: string) => [...new Set((file?.wanted ?? []).flatMap((w) => w.goes[c] ?? []).map((x) => x.replace(/^container$/, 'container env')))]

  const triggerTick = (n: string, e: string) => {
    if (!e || !wanted.get(n)?.manual[e]?.includes('trigger')) return null
    const m = marks.get(key(n, e))
    const v = stored.get(key(n, e)) ?? stored.get(key(n, ''))
    const stale = !!m && !!v && v.updatedAt > m.markedAt
    return (
      <label className={cn('mt-1 flex items-center gap-1 text-[10px]', !m || stale ? 'text-warn' : 'text-fg-subtle')}
        title={m ? `Ticked ${new Date(m.markedAt).toLocaleString()}${stale ? '; the value changed since, add it again' : ''}` : 'Trigger.dev env vars are set in Trigger.dev itself: add it there, then tick'}>
        <Checkbox className="size-3" disabled={!canWrite || tick.isPending} checked={!!m && !stale} onCheckedChange={(v) => tick.mutate({ name: n, env: e, on: v === true })} />
        {stale ? 'changed: re-add in Trigger.dev' : 'added in Trigger.dev'}
      </label>
    )
  }

  const valueCell = (n: string, e: string) => {
    const k = key(n, e)
    const s = stored.get(k)
    if (editing === k) return (
      <div className="flex gap-1">
        <Input autoFocus type="password" autoComplete="off" aria-label={`Value of ${n} for ${col(e).label}`} placeholder="Value" className="h-7 min-w-0 flex-1 font-mono text-xs"
          value={edit} onChange={(ev) => setEdit(ev.target.value)} onKeyDown={(ev) => { if (ev.key === 'Escape') setEditing(null); if (ev.key === 'Enter') { ev.preventDefault(); if (edit) set.mutate({ name: n, env: e, value: edit }) } }} />
        <Button size="sm" variant="primary" disabled={!edit || set.isPending} onClick={() => set.mutate({ name: n, env: e, value: edit })}>Save</Button>
      </div>
    )
    if (!applies(n, e)) return <span className="text-fg-faint" title="The project file does not send it here">not used</span>
    if (!s) {
      const inherits = e !== '' && stored.has(key(n, ''))
      return (
        <span className="flex items-center gap-1.5">
          {inherits ? <span className="text-fg-faint" title="Uses the All environments value">same as all</span>
            : needs(n, e) ? <span className="text-warn">missing</span> : <span className="text-fg-faint">—</span>}
          {canWrite && <SButton type="button" variant="link" size="xs" className="h-auto p-0 text-xs font-normal text-fg-subtle hover:text-fg" onClick={() => { setEditing(k); setEdit('') }}>{inherits ? 'override' : 'set'}</SButton>}
        </span>
      )
    }
    return (
      <span className="flex min-w-0 items-center gap-1.5">
        <span className="min-w-0 truncate font-mono text-fg select-all" title={`Updated ${new Date(s.updatedAt).toLocaleString()}`}>{shown[k] ?? `••••${s.last4 ?? ''}`}</span>
        {canWrite && <span className="flex shrink-0 gap-1.5 text-fg-subtle">
          {shown[k] !== undefined && <SButton type="button" variant="link" size="xs" className="h-auto p-0 text-xs font-normal text-fg-subtle hover:text-fg" onClick={() => void navigator.clipboard.writeText(shown[k]!)}>copy</SButton>}
          <SButton type="button" variant="link" size="xs" className="h-auto p-0 text-xs font-normal text-fg-subtle hover:text-fg" onClick={() => (shown[k] !== undefined ? hide(k) : view(n, e))}>{shown[k] !== undefined ? 'hide' : 'view'}</SButton>
          <SButton type="button" variant="link" size="xs" className="h-auto p-0 text-xs font-normal text-fg-subtle hover:text-fg" onClick={() => { setEditing(k); setEdit('') }}>edit</SButton>
          <SButton type="button" variant="link" size="xs" className="h-auto p-0 text-xs font-normal text-fg-subtle hover:text-err" onClick={() => void ask({ title: `Delete ${n} for ${col(e).label}?`, confirm: 'Delete', danger: true }).then((ok) => ok && remove.mutate({ name: n, env: e }))}>delete</SButton>
        </span>}
      </span>
    )
  }

  const row = (n: string) => {
    const w = wanted.get(n)
    const pre = splitPrefix(n)
    const target = pre && columns.includes(pre.env) && stored.has(key(n, '')) && !stored.has(key(pre.base, pre.env)) ? pre : null
    return (
      <tr key={n} className="align-top hover:bg-surface-2/40">
        <td className="py-2 pr-4">
          <div className="font-mono text-fg">{n}</div>
          {w?.description && <div className="text-[11px] text-fg-subtle">{w.description}</div>}
          <div className="mt-0.5 flex flex-wrap gap-1.5 text-[10px] text-fg-faint">
            {w && !w.required && <span>optional</span>}
            {!w && !hasAny(n) && <span className="text-warn">no value</span>}
            {target && canWrite && (
              <SButton type="button" variant="link" size="xs" className="h-auto p-0 text-[10px] font-normal text-accent" disabled={copy.isPending}
                title={`Copies the value; ${n} itself stays`} onClick={() => copy.mutate({ name: n, toName: target.base, toEnv: target.env })}>
                use as {target.base} for {col(target.env).label} →
              </SButton>
            )}
          </div>
        </td>
        {columns.map((c) => <td key={c} className="max-w-60 py-2 pr-4 text-xs">{valueCell(n, c)}{triggerTick(n, c)}</td>)}
      </tr>
    )
  }

  return (
    <section className="space-y-4 pb-16">
      <div className="text-sm text-fg-muted">
        Each secret has a value per environment. <span className="text-fg">All environments</span> is used wherever an environment has no value of its own.
        Values are never shown unless you click view; each view is recorded.
      </div>

      {file && (missingCount > 0 || file.extra.length > 0) && (
        <div className="flex flex-wrap gap-2 text-xs">
          {missingCount > 0 && <Button onClick={() => setFilter('missing')} className="border-warn/40 bg-warn/10 font-normal text-warn hover:bg-warn/20 hover:text-warn">{missingCount} need a value</Button>}
          {file.extra.length > 0 && <Button onClick={() => setFilter('extra')} className="font-normal text-fg-subtle">{file.extra.length} not in the project file</Button>}
        </div>
      )}
      {!file && rows && <div className="text-xs text-fg-subtle">No project file is applied yet, so where each secret goes is not known; the columns are the usual dev, uat and prod.</div>}

      <div className="flex flex-wrap items-center gap-2">
        <Input aria-label="Search secrets" placeholder="Search" className="h-8 w-56 font-mono" value={search} onChange={(e) => setSearch(e.target.value)} />
        <div role="tablist" className="flex gap-1 text-xs">
          {(['all', 'missing', 'extra'] as const).map((f) => (
            <Button key={f} variant="ghost" role="tab" aria-selected={filter === f} onClick={() => setFilter(f)}
              className={cn('font-normal', filter === f ? 'bg-surface-3 text-fg hover:bg-hover' : 'text-fg-muted')}>
              {f === 'all' ? 'All' : f === 'missing' ? 'Missing' : 'Not in file'}
            </Button>
          ))}
        </div>
      </div>

      {error && !rows ? <ErrorState error={error} retry={() => void refetch()} /> : !rows ? <SkeletonRows rows={5} /> : names.length === 0 ? <div className="py-3 text-xs text-fg-subtle">No secrets yet. Add one below, or paste a .env.</div> : (
        <div className="overflow-x-auto rounded-lg border border-border">
          <table className="w-full text-sm">
            <thead className="bg-surface-2">
              <tr className="text-left align-top">
                <th className="w-72 px-0 py-2 pr-4 pl-0 font-normal text-fg-subtle"><span className="pl-3">Name</span></th>
                {columns.map((c) => {
                  const to = goesTo(c)
                  return (
                    <th key={c} className="py-2 pr-4 font-normal">
                      <div className="text-fg">{col(c).label}</div>
                      <div className="text-[10px] text-fg-faint" title={to.join(', ')}>{to.length ? `→ ${to.slice(0, 3).join(', ')}${to.length > 3 ? ` +${to.length - 3}` : ''}` : col(c).hint}</div>
                    </th>
                  )
                })}
              </tr>
            </thead>
            {GROUPS.map((g) => {
              const inGroup = visible.filter((n) => (wanted.get(n)?.kind ?? 'none') === g.kind)
              if (!inGroup.length) return null
              return (
                <tbody key={g.kind} className="[&_td:first-child]:pl-3">
                  <tr><td colSpan={columns.length + 1} className="bg-surface-1 py-1.5 text-[11px] text-fg-subtle"><span className="font-medium text-fg-muted">{g.title}</span> · {g.hint}</td></tr>
                  {inGroup.map(row)}
                </tbody>
              )
            })}
          </table>
          {visible.length === 0 && <div className="p-3 text-xs text-fg-subtle">Nothing matches.</div>}
        </div>
      )}

      {file && file.envFiles.length > 0 && (
        <div className="text-xs text-fg-subtle">
          In Claude's container the dev values are also written to {file.envFiles.map((f) => <span key={f.path} className="mr-1 font-mono text-fg-muted" title={f.names.join(', ')}>{f.path}</span>)}
        </div>
      )}

      <RoleGate action="project.secrets.write" project={project}>
        <div className="rounded-lg border border-border bg-surface-2 p-3">
          <div className="mb-2 text-xs text-fg-subtle">Add a secret</div>
          <div className="flex flex-wrap gap-2" onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); if (name && value) set.mutate({ name, env: addEnv, value }) } }}>
            <Input aria-label="Secret name" placeholder="NAME" className="h-8 w-64 font-mono" value={name} onChange={(e) => setName(e.target.value.toUpperCase().replace(/[^A-Z0-9_]/g, '_'))} />
            <EnvSelect label="For" value={addEnv} columns={columns} onChange={setAddEnv} />
            <Input aria-label="Secret value" placeholder="Value" type="password" autoComplete="off" className="h-8 min-w-48 flex-1" value={value} onChange={(e) => setValue(e.target.value)} />
            <Button type="button" variant="primary" disabled={!name || !value || set.isPending} onClick={() => set.mutate({ name, env: addEnv, value })}>Add</Button>
          </div>
          <details className="mt-3">
            <summary className="cursor-pointer text-xs text-fg-subtle">Paste a .env (many at once)</summary>
            <Textarea aria-label="NAME=value lines" rows={6} spellCheck={false} autoComplete="off" className="mt-2 font-mono text-xs" placeholder={'STRIPE_KEY=sk_live_…\nVERCEL_TOKEN=…'} value={paste} onChange={(e) => setPaste(e.target.value)} />
            <div className="mt-2 flex items-center gap-2">
              <EnvSelect label="These values are for" value={pasteEnv} columns={columns} onChange={setPasteEnv} />
              <Button type="button" variant="primary" disabled={!paste.trim() || bulk.isPending} onClick={() => bulk.mutate({ text: paste, env: pasteEnv })}>Save all</Button>
              {bulk.data && <span className="text-xs text-fg-subtle">Saved {bulk.data.set.length}{bulk.data.skipped.length ? `; skipped ${bulk.data.skipped.map((x) => `line ${x.line} (${x.reason})`).join(', ')}` : ''}</span>}
            </div>
          </details>
        </div>
      </RoleGate>
      {(set.error || remove.error || bulk.error || tick.error || copy.error || !!viewError) && <ErrorState error={set.error ?? remove.error ?? bulk.error ?? tick.error ?? copy.error ?? viewError} compact />}
    </section>
  )
}

function EnvSelect({ label, value, columns, onChange }: { label: string; value: string; columns: string[]; onChange: (v: string) => void }) {
  return (
    <label className="flex items-center gap-1.5 text-xs text-fg-subtle">
      {label}
      <Select className="h-8" value={value} onChange={(e) => onChange(e.target.value)}>
        {columns.map((c) => <option key={c} value={c}>{col(c).label}</option>)}
      </Select>
    </label>
  )
}
